import * as React from 'react';

import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {Stack} from 'shells/layout/stack/stack';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {SetupContainer} from 'shells/setup/setup-container/setup-container';

import type {ChatAssignmentStrategyEnum, ThreadType} from 'nutshell-graphql-types';
import type {
    GetForm_form_createRules_lead as CreateRulesLeadFragment,
    GetInboundSmsRules_inboundSmsRules as InboundSmsRulesFragment,
    GetInboundFacebookRules_inboundFacebookRules as InboundFacebookRulesFragment,
    GetInboundWhatsAppRules_inboundWhatsAppRules as InboundWhatsAppRulesFragment,
    GetInboundEmailRules_inboundEmailRules as InboundEmailRulesFragment,
    InboundSmsRulesUpdateInput,
    InboundFacebookRulesUpdateInput,
    InboundWhatsAppRulesUpdateInput,
    InboundEmailRulesUpdateInput,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {RouteContext} from '../../route-context';

import {RedirectSidebarForm} from '../sms/inbound/redirect-phone-sidebar-form';
import {RegistrationCtaBanner} from '../sms/inbound/registration-cta-banner';

import {InboundMessageAssignment} from './inbound-message-assignment';
import {LeadCreationRules} from './lead-creation-rules';

import {RulesSidebarForm} from '.';

type Props = {
    inboundRules:
        | InboundSmsRulesFragment
        | InboundFacebookRulesFragment
        | InboundWhatsAppRulesFragment
        | InboundEmailRulesFragment;
    updateInboundRules: (
        input:
            | InboundSmsRulesUpdateInput
            | InboundFacebookRulesUpdateInput
            | InboundWhatsAppRulesUpdateInput
            | InboundEmailRulesUpdateInput
    ) => Promise<any>;
    isEnrolled: boolean;
    refetch: () => Promise<any>;
    threadType: ThreadType;
    hasAILeadCreation?: boolean;
    hasNewColumnLayout?: boolean;
};

export function InboundSettings(props: Props) {
    const {inboundRules} = props;
    const [assignmentRule, setAssignmentRule] = React.useState<ChatAssignmentStrategyEnum>(
        inboundRules && inboundRules.assignment ? inboundRules.assignment.strategy : 'ROUND_ROBIN'
    );
    const [assignedUserId, setAssignedUserId] = React.useState<string | null | undefined>(
        inboundRules && inboundRules.assignment && inboundRules.assignment.assignedUser
            ? inboundRules.assignment.assignedUser.id
            : null
    );
    const [createRulesSidebar, setCreateRulesSidebar] = React.useState<boolean>(false);
    const [showPhoneRedirectSidebar, setShowPhoneRedirectSidebar] = React.useState<boolean>(false);
    const [phoneRedirectSaveError, setPhoneRedirectSaveError] = React.useState<
        string | null | undefined
    >(null);
    const [isSavingRedirectPhone, setIsSavingRedirectPhone] = React.useState<boolean>(false);
    const [addTags, setAddTags] = React.useState<boolean>(
        inboundRules && inboundRules.leadCreation && inboundRules.leadCreation.tags
            ? inboundRules.leadCreation.tags.length > 0
            : false
    );
    const [isSavingAssignRules, setIsSavingAssignRules] = React.useState<boolean>(false);
    const [savingToastKey, setSavingToastKey] = React.useState<string | null | undefined>(null);

    const {toastState} = useManageSaveState(isSavingAssignRules, 1000);

    const {isEmbedded} = React.useContext(RouteContext);

    const createRules: CreateRulesLeadFragment | null | undefined =
        inboundRules.leadCreation && inboundRules.leadCreation.enabled
            ? {
                  __typename: 'FormCreateRulesLead',
                  tags:
                      (inboundRules &&
                          inboundRules.leadCreation &&
                          inboundRules.leadCreation.tags) ||
                      [],
                  stageset: inboundRules.leadCreation.stageset || null,
                  stagesetFieldRules: [],
              }
            : null;

    const redirectPhoneNumber =
        inboundRules.__typename === 'InboundSmsRules' &&
        inboundRules.redirectPhoneNumber &&
        inboundRules.redirectPhoneNumber.numberFormatted;

    const onSubmit = (values: any) => {
        props
            .updateInboundRules({
                leadCreation: {
                    enabled: values.enabled !== false,
                    tagIds: (addTags && values.tagIds) || [],
                    stagesetId:
                        values.stagesetId === 'auto-resolve' ? null : values.stagesetId || null,
                },
            })
            .then(() => {
                props.refetch();
                setCreateRulesSidebar(false);
                setShowPhoneRedirectSidebar(false);
            });
    };

    const handleRedirectPhoneSave = (phoneNumber?: string | null) => {
        setPhoneRedirectSaveError(null);
        setIsSavingRedirectPhone(true);

        // only track if the phone number is being set and not removed
        if (phoneNumber) {
            analyticsClient.track(
                analyticsClient.EVENTS.TRACK_INBOUND_SMS_REDIRECT_NUMBER_CONFIGURED
            );
        }

        return props
            .updateInboundRules({
                redirectPhoneNumber: phoneNumber,
            })
            .then(() => {
                props.refetch();
                setShowPhoneRedirectSidebar(false);
                setPhoneRedirectSaveError(null);
                setIsSavingRedirectPhone(false);
            })
            .catch((error) => {
                const gqlError = error.graphQLErrors[0] || null;
                const message = gqlError ? gqlError.message : 'An error occurred';
                setPhoneRedirectSaveError(message);
                setIsSavingRedirectPhone(false);
            });
    };

    const handleSaveAssignRules = (
        value: ChatAssignmentStrategyEnum,
        assigneeId?: string | null
    ) => {
        setAssignmentRule(value);
        setAssignedUserId(assigneeId);
        setIsSavingAssignRules(true);
        setSavingToastKey('inbound-sms-assignment');

        props
            .updateInboundRules({
                assignment: {
                    strategy: value,
                    assignedUserId: assigneeId,
                },
            })
            .then(() => {
                props.refetch();
                setIsSavingAssignRules(false);
            });
    };

    const inboundMessageAssignment = (
        <InboundMessageAssignment
            hasNewColumnLayout={props.hasNewColumnLayout ?? false}
            threadType={props.threadType}
            handleSaveAssignRules={handleSaveAssignRules}
            selectedValue={assignmentRule}
            assignedUserId={assignedUserId ?? null}
            setAssignmentRule={setAssignmentRule}
            savingToastKey={savingToastKey ?? ''}
            toastState={toastState}
        />
    );

    const leadCreationRules = (
        <LeadCreationRules
            hasAILeadCreation={props.hasAILeadCreation ?? false}
            threadType={props.threadType}
            hasNewColumnLayout={props.hasNewColumnLayout ?? false}
            createRules={createRules}
            setCreateRulesSidebar={setCreateRulesSidebar}
            setShowPhoneRedirectSidebar={setShowPhoneRedirectSidebar}
            redirectPhoneNumber={redirectPhoneNumber ? redirectPhoneNumber : null}
            isEmbedded={isEmbedded}
        />
    );

    return (
        <div className='flex flex-dir-col pb-16 gap-16'>
            {props.isEnrolled ? null : <RegistrationCtaBanner />}
            <div className={!props.isEnrolled ? 'disabled no-click' : 'full-height'}>
                {props.hasNewColumnLayout ? (
                    <Stack spacing={16}>
                        <SetupContainer
                            header='Lead creation rules'
                            subtitle='Automatically create leads from new inbound emails.'
                        >
                            {leadCreationRules}
                        </SetupContainer>
                        <SetupContainer
                            header='Inbound thread assignment'
                            subtitle='How new threads are assigned to team members'
                            savedState={
                                savingToastKey === 'inbound-sms-assignment' ? toastState : 'initial'
                            }
                        >
                            {inboundMessageAssignment}
                        </SetupContainer>
                    </Stack>
                ) : (
                    <PageContentTwoColumn
                        withoutOuterPadding={true}
                        overflowVisible={true}
                        leftContent={inboundMessageAssignment}
                        rightContent={leadCreationRules}
                    />
                )}
            </div>
            {showPhoneRedirectSidebar && (
                <RedirectSidebarForm
                    onClose={() => {
                        setShowPhoneRedirectSidebar(false);
                    }}
                    initialPhoneNumber={redirectPhoneNumber ? redirectPhoneNumber : undefined}
                    onDelete={() => handleRedirectPhoneSave(null)}
                    onSave={handleRedirectPhoneSave}
                    isSaving={isSavingRedirectPhone}
                    saveFailedErrorMessage={phoneRedirectSaveError}
                />
            )}
            {createRulesSidebar && !isEmbedded && (
                <RulesSidebarForm
                    // @ts-expect-error
                    onClose={() => setCreateRulesSidebar(false)}
                    onSubmit={onSubmit}
                    addTags={addTags}
                    setAddTags={setAddTags}
                    initialValues={{
                        tagIds: inboundRules.leadCreation.tags
                            ? inboundRules.leadCreation.tags.map((tag) => tag && tag.id)
                            : [],
                        stagesetId: inboundRules.leadCreation.stageset
                            ? inboundRules.leadCreation.stageset.id
                            : null,
                        addTags,
                    }}
                    threadType={props.threadType}
                    deleteProps={
                        createRules && {
                            onDelete: () =>
                                onSubmit({
                                    tagIds: [],
                                    stagesetId: null,
                                    addTags: false,
                                    enabled: false,
                                }),
                            buttonText: 'Remove lead rules',
                            title: 'Remove lead rules',
                            message: 'Are you sure you want to remove the lead rules?',
                        }
                    }
                />
            )}
        </div>
    );
}
